"use client";

import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProfileInfo from "./ProfileInfo";
import PortfolioTab from "./PortfolioTab";
import ExperienceTab from "./ExperienceTab";
import { ProfileData } from "@/hooks/useProfiles";
import { useIsMobile } from "@/hooks/use-mobile";
import EmptyStatePublic from "./EmptyStatePublic";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { trackProfileView } from "./utils/notificationTracker";
import { useSearchParams } from "next/navigation";
import { useCaseStudies } from "@/hooks/useCaseStudies";
import { useMemo } from "react";

// Define tab types to ensure consistency between triggers and content
type TabValue = "portfolio" | "experience" | "about";
interface ProfileTabsProps {
  profile: ProfileData;
  isSimplifiedView?: boolean;
  openPortfolioModal?: (item: import("@/hooks/profile").PortfolioItem) => void;
  onClosePortfolioModal?: () => void;
}
const ProfileTabs = ({
  profile,
  isSimplifiedView = false,
  openPortfolioModal,
  onClosePortfolioModal,
}: ProfileTabsProps) => {
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const tabParam = searchParams.get("tab");
    return (tabParam as TabValue) || "portfolio";
  });
  const [loadedTabs, setLoadedTabs] = useState<TabValue[]>(["portfolio", "experience", "about"]);
  const { user } = useAuth();

  // Fetch case studies for this profile
  const { data: caseStudies = [] } = useCaseStudies(profile?.id);

  // Track active tab for analytics purposes
  useEffect(() => {
    // Add to loaded tabs if not already loaded
    if (!loadedTabs.includes(activeTab)) {
      setLoadedTabs((prev) => [...prev, activeTab]);
    }
  }, [activeTab, profile.id, loadedTabs]);

  // Check if portfolio or experience tabs have content
  const hasPortfolioItems =
    profile.portfolio_items && profile.portfolio_items.length > 0;
  
  // Check if there are published case studies
  const hasCaseStudies = useMemo(() => 
    caseStudies.filter(cs => cs.status === 'published').length > 0,
    [caseStudies]
  );
  
  // Check if there's ANY portfolio content (portfolio items OR case studies)
  const hasAnyPortfolioContent = hasPortfolioItems || hasCaseStudies;
  
  const hasExperienceItems =
    profile.experience_items && profile.experience_items.length > 0;

  // Check if about section has content
  const hasAboutInfo = !!(
    profile.bio &&
    profile.bio.trim() !== "" &&
    profile.bio.length > 10
  );

  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue);
  };
  return (
    <div className="mt-8 md:mt-12">
      <Tabs
        defaultValue="portfolio"
        value={activeTab}
        className="w-full"
        onValueChange={handleTabChange}
      >
        <div className="flex justify-center py-[8px]">
          <TabsList className="inline-flex items-center border border-zinc-200 rounded-full bg-white overflow-hidden h-10">
            <TabsTrigger
              value="portfolio"
              className="px-4 py-1.5 text-[14px] font-medium rounded-full transition-all focus:outline-none
                data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-semibold
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
            >
              Portfolio
            </TabsTrigger>
            <TabsTrigger
              value="experience"
              className="px-4 py-1.5 text-[14px] font-medium rounded-full transition-all focus:outline-none
                data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-semibold
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
            >
              Experience
            </TabsTrigger>
            <TabsTrigger
              value="about"
              className="px-4 py-1.5 text-[14px] font-medium rounded-full transition-all focus:outline-none
                data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-semibold
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
            >
              About Me
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Only mount the active tab and previously accessed tabs */}
        {loadedTabs.includes("portfolio") && (
          <TabsContent forceMount value="portfolio" className="mt-6 data-[state=inactive]:hidden">
            {hasAnyPortfolioContent ? (
              <PortfolioTab
                profile={profile}
                hideTitle={true}
                openPortfolioModal={openPortfolioModal}
                onClosePortfolioModal={onClosePortfolioModal}
                caseStudies={caseStudies}
              />
            ) : (
              <EmptyStatePublic
                type="portfolio"
                username={profile.username}
                profileId={profile.id}
                profile={profile}
              />
            )}
          </TabsContent>
        )}

        {loadedTabs.includes("experience") && (
          <TabsContent forceMount value="experience" className="mt-6 data-[state=inactive]:hidden">
            {hasExperienceItems ? (
              <ExperienceTab profile={profile} hideTitle={true} />
            ) : (
              <EmptyStatePublic
                type="experience"
                username={profile.username}
                profileId={profile.id}
                profile={profile}
              />
            )}
          </TabsContent>
        )}

        {loadedTabs.includes("about") && (
          <TabsContent forceMount value="about" className="mt-6 data-[state=inactive]:hidden">
            {hasAboutInfo ? (
              <ProfileInfo profile={profile} hideTitle={true} />
            ) : (
              <EmptyStatePublic
                type="about"
                username={profile.username}
                profileId={profile.id}
                profile={profile}
              />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
export default ProfileTabs;
